import React, {useState} from "react";
import {Box, Button, ButtonBase, IconButton, InputBase, Menu, MenuItem, Popover, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Cancel01Icon, Search01Icon, Tick02Icon} from "@hugeicons/core-free-icons";

const ACTIVE_COLOR = "#4B55C9";

// Header text that opens a filter; highlighted while its filter is set
export const headLabelSx = (active) => ({
  height: 28,
  gap: 0.5,
  px: 0.75,
  mx: -0.75,
  borderRadius: "7px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.08em",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  color: active ? ACTIVE_COLOR : "#8A8AA0",
  bgcolor: active ? "rgba(132, 145, 247, 0.12)" : "transparent",
  "&:hover": {bgcolor: active ? "rgba(132, 145, 247, 0.18)" : "#F0F0F6", color: active ? ACTIVE_COLOR : "#4A4A63"},
});

// Header that turns into a search field on click; stays open while it has a value
export const SearchHeadFilter = ({label, value, onChange, placeholder}) => {
  const [editing, setEditing] = useState(false);

  if (!editing && !value) {
    return (
      <ButtonBase onClick={() => setEditing(true)} sx={headLabelSx(false)} aria-label={`Search by ${label.toLowerCase()}`}>
        {label}
        <HugeiconsIcon icon={Search01Icon} size={12}/>
      </ButtonBase>
    );
  }

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 0.5, height: 28, px: 0.75, mx: -0.75, maxWidth: 240,
      borderRadius: "7px", bgcolor: "#fff", border: "1px solid #8491F7", color: ACTIVE_COLOR,
    }}>
      <HugeiconsIcon icon={Search01Icon} size={13}/>
      <InputBase
        autoFocus={editing}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onChange("");
            setEditing(false);
          }
        }}
        inputProps={{"aria-label": `Search by ${label.toLowerCase()}`}}
        sx={{flex: 1, fontSize: 12, color: "#23264D", "& input": {p: 0}}}
      />
      {value && (
        <IconButton
          size={"small"}
          // keep the input focused so blur doesn't close it before the click lands
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onChange("");
            setEditing(false);
          }}
          sx={{p: 0.25, color: "#8A8AA0"}}
          aria-label={"Clear search"}
        >
          <HugeiconsIcon icon={Cancel01Icon} size={12}/>
        </IconButton>
      )}
    </Box>
  );
};

const formatSeconds = (total) => {
  const seconds = Math.round(total);
  if (seconds < 60) return `${seconds}s`;
  const rest = seconds % 60;
  return rest ? `${Math.floor(seconds / 60)}m ${rest}s` : `${seconds / 60}m`;
};

const durationInputSx = {
  height: 38, px: 1.5, borderRadius: "10px", bgcolor: "#FAFAFD", fontSize: 13, color: "#23264D",
  "&:focus-within": {bgcolor: "rgba(132, 145, 247, 0.08)"},
  "& input": {MozAppearance: "textfield"},
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {WebkitAppearance: "none", margin: 0},
};

// Header with a popover to set a min / max duration. min / max: seconds as text ("" = no limit).
export const DurationHeadFilter = ({label, min, max, onChange}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const active = min !== "" || max !== "";
  const invalid = min !== "" && max !== "" && Number(min) > Number(max);

  let pickedLabel = label;
  if (min !== "" && max !== "") pickedLabel = `${formatSeconds(min)} – ${formatSeconds(max)}`;
  else if (min !== "") pickedLabel = `≥ ${formatSeconds(min)}`;
  else if (max !== "") pickedLabel = `≤ ${formatSeconds(max)}`;

  return (
    <>
      <ButtonBase onClick={(e) => setAnchorEl(e.currentTarget)} sx={headLabelSx(active)} aria-label={`Filter by ${label.toLowerCase()}`}>
        <Box component={"span"} sx={{textTransform: active ? "none" : undefined, letterSpacing: active ? 0 : undefined, fontSize: active ? 12 : 11}}>
          {pickedLabel}
        </Box>
        <HugeiconsIcon icon={ArrowDown01Icon} size={12}/>
      </ButtonBase>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "14px", mt: 0.5, p: 2, width: 260}}}
      >
        <Typography sx={{mb: 1.25, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8491F7"}}>DURATION (SECONDS)</Typography>
        <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5}}>
          <InputBase type={"number"} placeholder={"Min"} value={min} onChange={(e) => onChange({min: e.target.value, max})}
                     inputProps={{min: 0, "aria-label": "Minimum duration in seconds"}} sx={durationInputSx} autoFocus/>
          <InputBase type={"number"} placeholder={"Max"} value={max} onChange={(e) => onChange({min, max: e.target.value})}
                     inputProps={{min: 0, "aria-label": "Maximum duration in seconds"}} sx={durationInputSx}/>
        </Box>
        <Typography sx={{mt: 1, fontSize: 12, color: invalid ? "#DC2626" : "#8A8AA0"}}>
          {invalid ? "Min is greater than max" : active ? pickedLabel : "Leave empty for no limit"}
        </Typography>
        {active && (
          <Button onClick={() => onChange({min: "", max: ""})} sx={{
            mt: 1.5, px: 1.5, borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 600,
            color: "#6B6B80", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
          }}>
            Clear
          </Button>
        )}
      </Popover>
    </>
  );
};

// Header with a menu of options. value: the picked value (single) or array of values (multiple); "" / [] = all.
export const SelectHeadFilter = ({label, value, options, onChange, multiple = false}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const picked = multiple ? value : (value ? [value] : []);
  const active = picked.length > 0;

  const pickedLabel = !active
    ? label
    : picked.length === 1
      ? options.find(option => option.value === picked[0])?.label || label
      : `${label} · ${picked.length}`;

  const toggle = (optionValue) => {
    if (!multiple) {
      onChange(optionValue === value ? "" : optionValue);
      setAnchorEl(null);
      return;
    }
    onChange(value.includes(optionValue) ? value.filter(item => item !== optionValue) : [...value, optionValue]);
  };

  return (
    <>
      <ButtonBase onClick={(e) => setAnchorEl(e.currentTarget)} sx={headLabelSx(active)} aria-label={`Filter by ${label.toLowerCase()}`}>
        <Box component={"span"} sx={{overflow: "hidden", textOverflow: "ellipsis", textTransform: active ? "none" : undefined, letterSpacing: active ? 0 : undefined, fontSize: active ? 12 : 11}}>
          {pickedLabel}
        </Box>
        <HugeiconsIcon icon={ArrowDown01Icon} size={12}/>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{sx: {borderRadius: "12px", mt: 0.5, minWidth: 190, maxHeight: 360}}}
      >
        <MenuItem
          dense
          selected={!active}
          onClick={() => {
            onChange(multiple ? [] : "");
            setAnchorEl(null);
          }}
          sx={{justifyContent: "space-between"}}
        >
          All
          {!active && <HugeiconsIcon icon={Tick02Icon} size={15}/>}
        </MenuItem>
        {options.length === 0 && (
          <Typography sx={{px: 2, py: 1, fontSize: 12, color: "#8A8AA0"}}>No options</Typography>
        )}
        {options.map(option => (
          <MenuItem
            key={option.value}
            dense
            selected={picked.includes(option.value)}
            onClick={() => toggle(option.value)}
            sx={{justifyContent: "space-between", gap: 2}}
          >
            {option.label}
            {picked.includes(option.value) && <HugeiconsIcon icon={Tick02Icon} size={15}/>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
